import { Entity } from '@api/api/entity-schema';
import { formatTicker } from '@api/mappers/formatTicker';
import { Button } from '@design/components/button/Button';
import { EntityDetails } from '@design/widgets/entity-details/EntityDetails';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { WatchlistTrigger } from '@terminal/components/watchlist/watchlist-trigger/WatchlistTrigger';
import { useWatchlistActions } from '@terminal/hooks/use-watchlist-actions/useWatchlistActions';
import { isCountryOrArea } from '@terminal/react-query-hooks/country-gdp/useCountryGdp';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { Badge } from '@toggle/design-system/src/components/badge/Badge';
import {
  Tooltip,
  TooltipTrigger,
} from '@toggle/design-system/src/components/tooltip/Tooltip';
import clsx from 'clsx';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './EntityHeader.module.scss';

export interface EntityHeaderProps {
  entity: Entity;
  priceDisplayNode: ReactNode;
}

const DEFAULT_ICON_SIZE = 48;
const MAX_WATCHLISTS = 3;

export const EntityHeader = ({
  entity,
  priceDisplayNode,
}: EntityHeaderProps) => {
  const { t } = useTranslation('widget');
  const { lists, listIds, isInAnyWatchlist } = useWatchlist(state => ({
    listIds: state.listIdsByTag.get(entity.tag),
    lists: state.lists,
    isInAnyWatchlist: !!state.listIdsByTag.get(entity.tag)?.length,
  }));
  const { navigateToWatchlist } = useWatchlistActions();

  const ticker = isCountryOrArea(entity) ? '' : formatTicker(entity);
  const entityName = entity.name_short || entity.name_full;
  const pillsWatchlist = lists
    .filter(list => listIds?.includes(list.id))
    .slice(0, MAX_WATCHLISTS);
  const countMoreWatchlists = Math.max(
    (listIds?.length ?? 0) - MAX_WATCHLISTS,
    0
  );
  const watchlistLabel = isInAnyWatchlist
    ? t('common:components.watchlistBtn.watching')
    : t('common:components.watchlistBtn.addToWatchlistLabel');

  return (
    <EntityDetails
      className={styles.styledEntityDetails}
      entityNameComponent={
        <>
          <span data-testid="text">{entityName}</span>
          <Tooltip
            inPortal
            isTouchDevice={false}
            label={watchlistLabel}
            trigger={[TooltipTrigger.Hover, TooltipTrigger.Focus]}
          >
            <div>
              <WatchlistTrigger
                tag={entity.tag}
                dropdownProps={{
                  strategy: 'fixed',
                  placement: 'top-start',
                  inPortal: false,
                }}
                renderTrigger={isInWatchlist => {
                  return (
                    <Button
                      className={clsx(styles.watchlistButton, {
                        [styles.active]: isInWatchlist,
                      })}
                      data-testid="watchlist-button"
                      size="xsmall"
                      variant="tertiary"
                      iconName={isInWatchlist ? 'Checkmark' : 'Add'}
                    />
                  );
                }}
              />
            </div>
          </Tooltip>
        </>
      }
      priceDisplayNode={priceDisplayNode}
      formattedTicker={ticker}
      watchlistPillsNode={
        pillsWatchlist.length > 0 && (
          <>
            {pillsWatchlist.map(({ id, name }) => (
              <Badge
                key={id}
                data-testid="entity-header-watchlist-pill"
                className={styles.pillsWatchlist}
                variant="textual"
                leftIcon="Watchlist"
                label={
                  <TruncateTooltip
                    inPortal={true}
                    text={name ?? ''}
                    atLength={20}
                  />
                }
                color="soft"
                onClick={() => navigateToWatchlist({ id })}
              />
            ))}
            {countMoreWatchlists > 0 && (
              <Badge
                data-testid="entity-header-watchlist-more"
                className={styles.moreWatchlists}
                variant="textual"
                label={t('widget:assetOverview.nMore', {
                  length: countMoreWatchlists,
                })}
                color="soft"
                onClick={() => navigateToWatchlist({ skipFallback: false })}
              />
            )}
          </>
        )
      }
      leftImgNode={
        <AssetLogoContainer size={DEFAULT_ICON_SIZE} entity={entity} />
      }
    />
  );
};
